import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { MdArticle, MdPeople, MdCampaign, MdVisibility, MdTrendingUp, MdKeyboardArrowDown, MdCalendarMonth, MdAutoAwesome, MdComment, MdFactCheck, MdFlag, MdArrowForward, MdPublic, MdGroup, MdWarningAmber } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import GlobalDateFilter from '../components/GlobalDateFilter';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const LOW_ACTIVITY_FILTER_LABELS = {
  today: 'Today', yesterday: 'Yesterday', last7: 'Last 7 Days', last30: 'Last 30 Days',
  thisMonth: 'This Month', lastMonth: 'Last Month', custom: 'Custom Range',
};

export default function Dashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today');
  const [prevFilter, setPrevFilter] = useState('today');
  const [skipFetch, setSkipFetch] = useState(false);

  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [error, setError] = useState('');
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [articleTab, setArticleTab] = useState('today');

  const articleTabs = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 Days' },
    { key: 'last30', label: 'Last 30 Days' },
  ];
  const dropdownRef = useRef(null);

  // Chart data states
  const [readsData, setReadsData] = useState([]);
  const [readsGranularity, setReadsGranularity] = useState('day');
  const [regionData, setRegionData] = useState([]);
  const [topArticles, setTopArticles] = useState({ today: [], yesterday: [], last7: [], last30: [] });
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [userGrowthGranularity, setUserGrowthGranularity] = useState('month');
  const [chartsLoading, setChartsLoading] = useState(true);

  // Reporters leaderboard state
  const [reportersLeaderboard, setReportersLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  // Posts per category state
  const [categoriesPosts, setCategoriesPosts] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Low-activity categories/cities (reuses the same report APIs as the
  // Category Report / City Report pages — no new backend endpoint needed).
  const [lowActivityFilter, setLowActivityFilter] = useState('today');
  const [lowActivityCustom, setLowActivityCustom] = useState({ from: '', to: '' });
  const [lowActivityThreshold, setLowActivityThreshold] = useState(5);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [cityCounts, setCityCounts] = useState([]);
  const [lowActivityLoading, setLowActivityLoading] = useState(true);
  const [showLowCategories, setShowLowCategories] = useState(false);
  const [showLowCities, setShowLowCities] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch chart data — re-runs when the dashboard filter changes so the
  // Users vs Guests trend stays in sync with the KPI cards.
  useEffect(() => {
    setChartsLoading(true);

    // Build query params matching the same filter as the KPI overview call
    const chartParams = { filter };
    if (filter === 'custom' && customFromDate && customToDate) {
      chartParams.from_date = customFromDate;
      chartParams.to_date   = customToDate;
    }

    // articleTab keys map to the API's expected filter values
    const filterMap = { today: 'today', yesterday: 'yesterday', last7: 'last7days', last30: 'last30days' };

    Promise.all([
      api.get('/admin/analytics/reads-trend', { params: chartParams }),
      api.get('/admin/analytics/users-by-region', { params: { limit: 10 } }),
      // Pass same filter as overview so chart values match KPI cards
      api.get('/admin/analytics/users-vs-guests', { params: chartParams }),
      api.get('/admin/analytics/most-viewed-articles', { params: { filter: filterMap.today } }),
      api.get('/admin/analytics/most-viewed-articles', { params: { filter: filterMap.yesterday } }),
      api.get('/admin/analytics/most-viewed-articles', { params: { filter: filterMap.last7 } }),
      api.get('/admin/analytics/most-viewed-articles', { params: { filter: filterMap.last30 } }),
    ]).then(([reads, regions, uvg, todayArticles, yesterdayArticles, last7Articles, last30Articles]) => {
      // Reads trend: [{ date, reads }], granularity: 'day' | 'month'
      const readsGranularity = reads.data.granularity || 'day';
      const processedReads = (reads.data.data || []).map(item => ({
        day: readsGranularity === 'month'
          ? new Date(item.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
          : new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        reads: item.reads || 0
      }));
      setReadsData(processedReads);
      setReadsGranularity(readsGranularity);

      // Users by region: [{ city, users }]
      const processedRegions = (regions.data.data || []).map(item => ({
        region: item.city || 'Unknown',
        users: item.users || 0
      }));
      setRegionData(processedRegions);

      // Users vs guests: [{ label, users, guests }], granularity: 'day' | 'month'
      // The backend also returns periodTotals that match the overview KPI cards.
      setUserGrowthData(uvg.data.data || []);
      setUserGrowthGranularity(uvg.data.granularity || 'month');

      // Most viewed articles: [{ rank, title, views }]
      const mapArticles = (res) => (res.data.data || []).map(item => ({
        title: item.title || 'Untitled',
        views: item.views || 0
      }));
      setTopArticles({
        today: mapArticles(todayArticles),
        yesterday: mapArticles(yesterdayArticles),
        last7: mapArticles(last7Articles),
        last30: mapArticles(last30Articles),
      });
    }).catch(err => {
      console.error('Error loading chart data:', err);
    }).finally(() => {
      setChartsLoading(false);
    });
  // Re-fetch whenever the KPI filter changes so chart stays in sync
  }, [filter, customFromDate, customToDate]);

  // Fetch reporters leaderboard for today
  useEffect(() => {
    setLeaderboardLoading(true);
    api.get('/admin/analytics/reporters-leaderboard', { params: { filter: 'today' } })
      .then(res => {
        const leaderboard = (res.data.data || []).map(item => ({
          id: item.rank,
          name: item.reporter || 'Unknown',
          posted: item.posted || 0
        }));
        setReportersLeaderboard(leaderboard);
      })
      .catch(err => console.error('Error loading reporters leaderboard:', err))
      .finally(() => setLeaderboardLoading(false));
  }, []);

  // Fetch posts per category (Top 3, with 7-day daily breakdown)
  useEffect(() => {
    setCategoriesLoading(true);
    api.get('/admin/analytics/posts-per-category', { params: { days: 7 } })
      .then(res => {
        const sorted = (res.data.data || []).map(cat => ({
          name: cat.category || 'Uncategorized',
          total: cat.total || 0,
          daysList: cat.daily || []
        }));
        setCategoriesPosts(sorted);
      })
      .catch(err => console.error('Error loading categories:', err))
      .finally(() => setCategoriesLoading(false));
  }, []);

  // Fetch category/city post counts for the low-activity summary
  useEffect(() => {
    setLowActivityLoading(true);
    const params = { filter: lowActivityFilter };
    if (lowActivityFilter === 'custom' && lowActivityCustom.from && lowActivityCustom.to) {
      params.from_date = lowActivityCustom.from;
      params.to_date = lowActivityCustom.to;
      params.startDate = lowActivityCustom.from;
      params.endDate = lowActivityCustom.to;
    }
    Promise.all([
      api.get('/admin/reports/category-today', { params }),
      api.get('/admin/reports/city-today', { params }),
    ]).then(([catRes, cityRes]) => {
      setCategoryCounts(catRes.data.data || []);
      setCityCounts(cityRes.data.data || []);
    }).catch(err => {
      console.error('Error loading low-activity report:', err);
    }).finally(() => setLowActivityLoading(false));
  }, [lowActivityFilter, lowActivityCustom.from, lowActivityCustom.to]);

  const lowCategories = categoryCounts.filter(r => r.todayPosts < lowActivityThreshold);
  const lowCities = cityCounts.filter(r => r.todayPosts < lowActivityThreshold);

  const filterOptions = [
    { value: 'total', label: 'Total' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const selectedLabel = filterOptions.find(o => o.value === filter)?.label || 'Today';

  const fetchStats = () => {
    setLoading(true);
    setError('');
    let url = `/admin/analytics/overview?filter=${filter}`;
    
    if (filter === 'custom') {
      if (!customFromDate || !customToDate) {
        setError('Both From Date and To Date are required.');
        setLoading(false);
        return;
      }
      
      const from = new Date(customFromDate);
      const to = new Date(customToDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      if (from > to) {
        setError('From Date cannot be greater than To Date.');
        setLoading(false);
        return;
      }
      if (from > today || to > today) {
        setError('Future dates are not allowed.');
        setLoading(false);
        return;
      }
      
      url += `&from_date=${customFromDate}&to_date=${customToDate}`;
    }
    
    api.get(url)
      .then(res => setStats(res.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Keep "Online Now" (and the other cards) fresh without a manual refresh.
  const fetchStatsRef = useRef(fetchStats);
  fetchStatsRef.current = fetchStats;

  useEffect(() => {
    const intervalId = setInterval(() => fetchStatsRef.current(), 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (skipFetch) {
      setSkipFetch(false);
      return;
    }
    if (filter !== 'custom') {
      setCustomFromDate('');
      setCustomToDate('');
      setError('');
      setShowCustomPanel(false);
      fetchStats();
    } else {
      setShowCustomPanel(true);
    }
  }, [filter]);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    if (newFilter === 'custom') {
      setPrevFilter(filter);
    }
    setFilter(newFilter);
  };

  const handleApplyCustom = () => {
    if (!customFromDate || !customToDate) {
      setError('Both From Date and To Date are required.');
      return;
    }
    const from = new Date(customFromDate);
    const to = new Date(customToDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (from > to) {
      setError('From Date cannot be greater than To Date.');
      return;
    }
    if (from > today || to > today) {
      setError('Future dates are not allowed.');
      return;
    }

    fetchStats();
    setShowCustomPanel(false);
  };

  const handleCancelCustom = () => {
    setShowCustomPanel(false);
    setError('');
    setSkipFetch(true);
    setFilter(prevFilter);
  };

  const cards = [
    { label: 'Articles', value: stats?.articlesToday || 0, icon: MdArticle, color: '#DC2626' },
    { label: 'Users', value: stats?.totalUsers || 0, icon: MdPeople, color: '#10B981' },
    { label: 'Reads', value: stats?.readsToday || 0, icon: MdVisibility, color: '#6366F1' },
    { label: 'Readers', value: stats?.readersToday || 0, icon: MdGroup, color: '#0EA5E9' },
    { label: 'Guest Devices', value: stats?.totalGuests || 0, icon: MdTrendingUp, color: '#8B5CF6' },
    { label: 'Online Now', value: stats?.onlineNow || 0, icon: MdPublic, color: '#F59E0B' },
  ];

  return (
    <div className="page dashboard-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showCustomPanel ? '10px' : '20px', flexWrap: 'wrap', gap: '24px' }}>
        <div className="dash-tagline" style={{ fontFamily: 'Ramabhadra, serif', fontSize: '28px', fontWeight: '400', lineHeight: '1.5', flex: '1', minWidth: '0' }}>
          ప్రతి ఉదయం మీకు చేరే విశ్వసనీయ వార్తల వేదిక.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div className="date-filter" ref={dropdownRef} style={{ position: 'relative', width: 'fit-content' }}>
            <button
              className={`dashboard-date-trigger ${isDropdownOpen ? 'open' : ''}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <MdCalendarMonth className="dashboard-date-icon" />
              <span style={{ fontWeight: '600' }}>{selectedLabel}</span>
              {filter === 'custom' && customFromDate && customToDate && (
                <span className="dashboard-date-range">
                  ({customFromDate} to {customToDate})
                </span>
              )}
              <MdKeyboardArrowDown className="dashboard-date-caret" style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'none' }} />
            </button>

            {isDropdownOpen && (
              <div className="dashboard-date-menu">
                {filterOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`dashboard-date-option ${filter === opt.value ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange({ target: { value: opt.value } });
                      setIsDropdownOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showCustomPanel && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <input
            type={customFromDate ? 'date' : 'text'}
            placeholder="Start Date 📅"
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (!e.target.value && (e.target.type = 'text'))}
            value={customFromDate}
            onChange={(e) => setCustomFromDate(e.target.value)}
            className="dash-custom-input"
            style={{
              height: '42px',
              padding: '0 16px',
              borderRadius: '8px',
              outline: 'none',
              width: '160px',
              fontSize: '14px',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          />
          <input
            type={customToDate ? 'date' : 'text'}
            placeholder="End Date 📅"
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (!e.target.value && (e.target.type = 'text'))}
            value={customToDate}
            onChange={(e) => setCustomToDate(e.target.value)}
            className="dash-custom-input"
            style={{
              height: '42px',
              padding: '0 16px',
              borderRadius: '8px',
              outline: 'none',
              width: '160px',
              fontSize: '14px',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          />
          <button
            onClick={handleApplyCustom}
            className="btn btn-primary"
            style={{
              height: '42px',
              padding: '0 24px',
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          >
            Apply
          </button>
          <button
            onClick={handleCancelCustom}
            className="btn btn-ghost"
            style={{
              height: '42px',
              padding: '0 24px',
              margin: 0,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          >
            Cancel
          </button>
          {error && <span style={{ color: 'red', fontSize: '14px', marginLeft: '10px', fontWeight: '500' }}>{error}</span>}
        </div>
      )}

      {!showCustomPanel && error && <div style={{ color: 'red', marginBottom: '15px', fontWeight: 'bold' }}>{error}</div>}
      {loading && stats ? <div style={{opacity: 0.5}}>Updating...</div> : null}
      
      <div className="stats-grid stats-grid-6">
        {cards.map((card, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ color: card.color, fontSize: '24px' }}>
              <card.icon />
            </div>
            <div className="stat-info">
              <h3 style={{ color: card.color }}>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Low-Activity Categories & Cities */}
      <div className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '4px' }}>
          <h3 className="chart-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MdWarningAmber style={{ color: '#F59E0B' }} /> Low-Activity Categories &amp; Cities
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Fewer than
              <input
                type="number"
                min={1}
                value={lowActivityThreshold}
                onChange={e => setLowActivityThreshold(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: '52px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px', textAlign: 'center' }}
              />
              posts
            </label>
            <GlobalDateFilter
              filter={lowActivityFilter}
              fromDate={lowActivityCustom.from}
              toDate={lowActivityCustom.to}
              onChange={(f, from, to) => { setLowActivityFilter(f); setLowActivityCustom({ from, to }); }}
            />
          </div>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.4 }}>
          Based on the same report data as Category Report / City Report, for the selected period.
        </p>

        {lowActivityLoading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>Loading...</div>
        ) : (
          <div className="dash-low-activity-grid">
            <div className="dash-warn-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                  <div className="dash-warn-figure" style={{ fontSize: '26px', fontWeight: 700, color: '#92400E' }}>{lowCategories.length}</div>
                  <div className="dash-warn-copy" style={{ fontSize: '13px', color: '#78350F' }}>
                    of {categoryCounts.length} categories have &lt; {lowActivityThreshold} posts
                    <strong> ({LOW_ACTIVITY_FILTER_LABELS[lowActivityFilter] || 'selected period'})</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                  <button
                    className="btn btn-xs btn-ghost"
                    style={{ border: '1px solid #FDE68A', color: '#92400E' }}
                    onClick={() => setShowLowCategories(s => !s)}
                    disabled={lowCategories.length === 0}
                  >
                    {showLowCategories ? 'Hide list' : 'Show list'}
                  </button>
                  <Link to="/reports/category" className="dash-warn-link" style={{ fontSize: '12px', color: '#92400E', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    Full report <MdArrowForward size={12} />
                  </Link>
                </div>
              </div>
              {showLowCategories && lowCategories.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                  {lowCategories.map(r => (
                    <span key={r.category} className="dash-chip" style={{ fontSize: '12px', background: '#FEF3C7', color: '#92400E', padding: '3px 9px', borderRadius: '99px', fontWeight: 500 }}>
                      {r.category} ({r.todayPosts})
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="dash-danger-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                  <div className="dash-warn-figure" style={{ fontSize: '26px', fontWeight: 700, color: '#991B1B' }}>{lowCities.length}</div>
                  <div className="dash-warn-copy" style={{ fontSize: '13px', color: '#7F1D1D' }}>
                    of {cityCounts.length} cities have &lt; {lowActivityThreshold} posts
                    <strong> ({LOW_ACTIVITY_FILTER_LABELS[lowActivityFilter] || 'selected period'})</strong>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-end', flexShrink: 0 }}>
                  <button
                    className="btn btn-xs btn-ghost"
                    style={{ border: '1px solid #FECACA', color: '#991B1B' }}
                    onClick={() => setShowLowCities(s => !s)}
                    disabled={lowCities.length === 0}
                  >
                    {showLowCities ? 'Hide list' : 'Show list'}
                  </button>
                  <Link to="/reports/city" className="dash-danger-link" style={{ fontSize: '12px', color: '#991B1B', display: 'flex', alignItems: 'center', gap: '2px' }}>
                    Full report <MdArrowForward size={12} />
                  </Link>
                </div>
              </div>
              {showLowCities && lowCities.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                  {lowCities.map(r => (
                    <span key={r.city} className="dash-chip" style={{ fontSize: '12px', background: '#FEE2E2', color: '#991B1B', padding: '3px 9px', borderRadius: '99px', fontWeight: 500 }}>
                      {r.city} ({r.todayPosts})
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 70-30 Layout: Charts and Leaderboard */}
      <div className="dashboard-main-grid">
        {/* 70% Column - Charts */}
        <div className="dashboard-main-col">
      {/* Reads Chart - Full Width */}
      <div className="dashboard-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
          <h3 className="chart-title" style={{ margin: 0 }}>
            Reads Analytics
            {' – '}
            {filter === 'custom' && customFromDate && customToDate
              ? `${customFromDate} to ${customToDate}`
              : selectedLabel}
          </h3>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg)', padding: '2px 8px', borderRadius: '99px', fontWeight: 500 }}>
            {readsGranularity === 'month' ? 'monthly' : 'daily'}
          </span>
        </div>
        {chartsLoading ? (
          <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            Loading chart data...
          </div>
        ) : readsData.length === 0 ? (
          <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            No data available
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={readsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorReads" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#EF233C" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#EF233C" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="0" stroke="#F0F4F8" vertical={false} />
            <XAxis dataKey="day" stroke="#64748B" style={{ fontSize: '14px' }} />
            <YAxis stroke="#64748B" style={{ fontSize: '14px' }} />
            <Tooltip
              contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
              cursor={{ stroke: '#EF233C', strokeWidth: 2 }}
            />
            <Area type="monotone" dataKey="reads" stroke="#EF233C" strokeWidth={2} fillOpacity={1} fill="url(#colorReads)" />
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>

      {/* User Growth Chart - Users vs Guests */}
      <div className="dashboard-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
          <h3 className="chart-title" style={{ margin: 0 }}>Users vs Guests Trend</h3>
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)', background: 'var(--bg)', padding: '2px 8px', borderRadius: '99px', fontWeight: 500 }}>
            {filter === 'total' ? 'All-time' : selectedLabel} · {userGrowthGranularity === 'day' ? 'daily' : 'monthly'}
          </span>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.4 }}>
          New registrations {userGrowthGranularity === 'day' ? 'per day' : 'per month'} within the selected period.
          {filter === 'total' ? ' KPI cards show all-time totals; chart shows a recent trend.' : ' Chart bars sum = KPI card counts.'}
        </p>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={userGrowthData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="0" stroke="#F0F4F8" vertical={false} />
            <XAxis dataKey="label" stroke="#64748B" style={{ fontSize: '14px' }} />
            <YAxis stroke="#64748B" style={{ fontSize: '14px' }} />
            <Tooltip
              contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
              cursor={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
            />
            <Legend />
            <Line type="monotone" dataKey="users" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 4 }} name="Users" />
            <Line type="monotone" dataKey="guests" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', r: 4 }} name="Guests" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Two Column Layout: Articles & Region Progress Bars */}
      <div className="dashboard-sub-grid">
        {/* Top Articles with Tabs */}
        <div className="dashboard-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 className="chart-title" style={{ margin: 0 }}>Most Viewed Articles</h3>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {articleTabs.map(tab => (
                <button
                  key={tab.key}
                  className={`dash-pill ${articleTab === tab.key ? 'dash-pill-active' : ''}`}
                  onClick={() => setArticleTab(tab.key)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: articleTab === tab.key ? '2px solid #EF233C' : '1px solid #E2E8F0',
                    background: articleTab === tab.key ? '#FFF1F2' : '#fff',
                    color: articleTab === tab.key ? '#EF233C' : '#64748B',
                    fontSize: '14px',
                    fontWeight: articleTab === tab.key ? '600' : '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="dash-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rank</th>
                  <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title</th>
                  <th style={{ textAlign: 'right', padding: '10px', fontWeight: '600', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Views</th>
                </tr>
              </thead>
              <tbody>
                {topArticles[articleTab].map((article, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '10px', fontWeight: '500', fontSize: '17px', fontFamily: 'Ramabhadra, serif' }}>{idx + 1}</td>
                    <td style={{ padding: '10px', fontSize: '17px', fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Ramabhadra, serif' }}>{article.title}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#DC2626', fontWeight: '500', fontSize: '17px', fontFamily: 'Ramabhadra, serif' }}>{(article.views || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Users by Region - Progress Bars */}
        <div className="dashboard-card">
          <h3 className="chart-title" style={{ marginBottom: '20px' }}>Users by Region - Top 10</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {regionData.slice(0, 10).map((item, idx) => {
              const maxUsers = Math.max(...regionData.slice(0, 10).map(r => r.users));
              const percentage = (item.users / maxUsers) * 100;
              return (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="dash-region-label" style={{ fontSize: '15px', fontWeight: '500', color: 'var(--text)' }}>{idx + 1}. {item.region}</span>
                    <span className="dash-region-count" style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)' }}>{item.users.toLocaleString()}</span>
                  </div>
                  <div className="dash-bar-track" style={{
                    height: '8px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${percentage}%`,
                      background: `linear-gradient(90deg, #8B5CF6, #6366F1)`,
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
        </div>

        {/* 30% Column - Quick Links & Leaderboard */}
        <div className="dashboard-side-col">
          {/* Quick Links */}
          <div className="dashboard-card">
            <h3 className="chart-title" style={{ marginBottom: '16px' }}>Quick Links</h3>
            <div className="dash-quick-links-grid">
              {[
                { icon: MdAutoAwesome, label: 'Status Maker', path: '/status', color: '#6366F1' },
                { icon: MdComment, label: 'Comments', path: '/comments', color: '#EC4899' },
                { icon: MdFactCheck, label: 'Contributor News', path: '/contributor-review', color: '#F59E0B' },
                { icon: MdFlag, label: 'Moderation', path: '/moderation', color: '#EF4444' },
              ].map((link, idx) => (
                <Link key={idx} to={link.path} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px 12px',
                  borderRadius: '12px',
                  background: `${link.color}12`,
                  border: `1.5px solid ${link.color}24`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = `${link.color}20`;
                  e.currentTarget.style.borderColor = `${link.color}40`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = `${link.color}12`;
                  e.currentTarget.style.borderColor = `${link.color}24`;
                }}
                >
                  <link.icon style={{ fontSize: '24px', color: link.color, flexShrink: 0 }} />
                  <span className="dash-tile-label" style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-word', maxWidth: '100%' }}>
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

        {/* Reporters Leaderboard */}
        <div className="dashboard-card">
          <h3 className="chart-title" style={{ marginBottom: '20px' }}>Reporters Leaderboard - Today</h3>
          {leaderboardLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              Loading leaderboard...
            </div>
          ) : reportersLeaderboard.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              No reporters yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {reportersLeaderboard.map((reporter, idx) => (
                <div
                  key={reporter.id}
                  className={idx === 0 ? 'dash-rank-1' : idx === 1 ? 'dash-rank-2' : idx === 2 ? 'dash-rank-3' : ''}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: idx === 0 ? '#FFF1F2' : idx === 1 ? '#F0F9FF' : idx === 2 ? '#FFFBEB' : 'transparent',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#E2E8F0'}`,
                    transition: 'all 0.2s ease',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <div className={idx > 2 ? 'dash-rank-badge-default' : ''} style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: ['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#E2E8F0',
                      color: idx > 2 ? '#475569' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '16px',
                      flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p className="dash-rank-name" style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {reporter.name}
                      </p>
                      <p className="dash-rank-sub" style={{ margin: '2px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                        Posted
                      </p>
                    </div>
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: ['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#6366F1',
                    minWidth: 'fit-content',
                    marginLeft: '8px'
                  }}>
                    {reporter.posted}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Posts Per Category */}
        <div className="dashboard-card">
          <h3 className="chart-title" style={{ marginBottom: '16px' }}>Posts Per Category - Top 3</h3>
          {categoriesLoading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              Loading categories...
            </div>
          ) : categoriesPosts.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '20px' }}>
              No posts available
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {categoriesPosts.map((category, idx) => {
                const categoryColors = ['#DC2626', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];
                const mainColor = categoryColors[idx % categoryColors.length];
                return (
                <div key={category.name} style={{
                  padding: '12px',
                  background: `${mainColor}08`,
                  borderRadius: '10px',
                  border: `1px solid ${mainColor}20`,
                  transition: 'all 0.2s ease',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span className="dash-category-name" style={{ fontSize: '24px', fontWeight: '400', color: 'var(--text)', maxWidth: '70%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Ramabhadra, serif' }}>
                      {category.name}
                    </span>
                    <span style={{ fontSize: '24px', fontWeight: '500', color: '#fff', background: mainColor, padding: '4px 16px', borderRadius: '8px', fontFamily: 'Ramabhadra, serif', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {category.total}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {category.daysList.map((day, dayIdx) => (
                      <div key={dayIdx} style={{
                        flex: 1,
                        minWidth: '0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                      }}>
                        <div className={day.count > 0 ? '' : 'dash-daycell-empty'} style={{
                          width: '100%',
                          height: '28px',
                          background: day.count > 0 ? mainColor : '#E2E8F0',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          fontWeight: '400',
                          color: day.count > 0 ? '#fff' : '#94A3B8',
                          transition: 'all 0.2s ease',
                          fontFamily: 'Ramabhadra, serif',
                        }}>
                          {day.count > 0 ? day.count : '-'}
                        </div>
                        <span className="dash-region-count" style={{ fontSize: '15px', color: 'var(--text-secondary)', fontWeight: '400', fontFamily: 'Ramabhadra, serif' }}>
                          {day.date}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
        </div>
      </div>

    </div>
  );
}
